import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import "../styles/fetchorder.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch, faFileInvoice, faCalendarAlt, faUser, faBox, faTag,
    faCalculator, faExclamationTriangle, faTrash, faTimes, faSpinner,
    faCheckCircle, faExclamationCircle, faMoneyBill, faBoxes, faStore,
    faFilter, faEdit, faSave, faEye, faSync, faPlus, faMinus, faRupeeSign,
    faChevronDown, faShoppingCart, faClock, faTruck, faCircleCheck,
    faCircleInfo, faCircleExclamation, faExchangeAlt, faPalette, faWarehouse,
    faRulerCombined, faLayerGroup, faPercent, faHistory, faReceipt,
    faDownload, faCheckDouble
} from '@fortawesome/free-solid-svg-icons';

// ==============================================
// Translation Utility (same as your existing)
// ==============================================
// ... (keep your existing translation code)

// ==============================================
// Toast Notification Component
// ==============================================
// ... (keep your existing Toast component)

// ==============================================
// Shop Selector Modal Component
// ==============================================
// ... (keep your existing ShopSelectorModal)

// ==============================================
// Approval Modal Component
// ==============================================
const ApprovalModal = ({ isOpen, onClose, item, onConfirm, translations, isRTL, loading }) => {
    const [approvalType, setApprovalType] = useState('full');
    const [partialQuantity, setPartialQuantity] = useState('');
    const [localLoading, setLocalLoading] = useState(false);

    useEffect(() => {
        if (item) {
            setApprovalType('full');
            setPartialQuantity('');
        }
    }, [item]);

    if (!isOpen || !item) return null;

    const maxQuantity = parseFloat(item.remaining_quantity) || parseFloat(item.ordered_quantity) || 0;
    const currentApproved = parseFloat(item.approved_quantity) || 0;
    const orderedQty = parseFloat(item.ordered_quantity) || 0;
    const unit = item.unit || 'yards';

    const handleConfirm = async () => {
        setLocalLoading(true);
        if (approvalType === 'partial') {
            const qty = parseFloat(partialQuantity);
            if (isNaN(qty) || qty <= 0) {
                alert('Please enter a valid quantity');
                setLocalLoading(false);
                return;
            }
            if (qty > maxQuantity) {
                alert(`Quantity cannot exceed remaining quantity (${maxQuantity} ${unit})`);
                setLocalLoading(false);
                return;
            }
            await onConfirm({ type: 'partial', quantity: qty });
        } else {
            await onConfirm({ type: 'full', quantity: maxQuantity });
        }
        setLocalLoading(false);
    };

    const isProcessing = loading || localLoading;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content approval-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                <div className="modal-header">
                    <h3><FontAwesomeIcon icon={faCheckCircle} style={{ color: '#10b981' }} />{translations?.approveStatus || 'Approve Item'}</h3>
                    <button className="modal-close" onClick={onClose}><FontAwesomeIcon icon={faTimes} /></button>
                </div>
                <div className="modal-body">
                    <div className="approval-item-info">
                        <p><strong>{translations?.productName || 'Product'}:</strong> {item.product_name}</p>
                        <p><strong>{translations?.colorCode || 'Color'}:</strong> {item.product_colorcode || '-'}</p>
                        <p><strong>{translations?.quantity || 'Order Quantity'}:</strong> {orderedQty} {unit}</p>
                        {currentApproved > 0 && (
                            <p><strong>{translations?.alreadyApproved || 'Already Approved'}:</strong> {currentApproved} {unit}</p>
                        )}
                        <p><strong>{translations?.remainingQuantity || 'Remaining'}:</strong> {maxQuantity} {unit}</p>
                    </div>

                    <div className="approval-type-selector">
                        <label className="approval-option">
                            <input type="radio" name="approvalType" value="full" checked={approvalType === 'full'} onChange={() => setApprovalType('full')} />
                            <div className="approval-option-content">
                                <FontAwesomeIcon icon={faCheckDouble} />
                                <span>{translations?.fullApproval || 'Full Approval'}</span>
                                <small>Approve the entire remaining quantity ({maxQuantity} {unit})</small>
                            </div>
                        </label>
                        <label className="approval-option">
                            <input type="radio" name="approvalType" value="partial" checked={approvalType === 'partial'} onChange={() => setApprovalType('partial')} />
                            <div className="approval-option-content">
                                <FontAwesomeIcon icon={faPercent} />
                                <span>{translations?.partialApproval || 'Partial Approval'}</span>
                                <small>Approve a specific quantity</small>
                            </div>
                        </label>
                    </div>

                    {approvalType === 'partial' && (
                        <div className="partial-quantity-input">
                            <label>{translations?.approveQuantity || 'Quantity to Approve'}:</label>
                            <input
                                type="number"
                                value={partialQuantity}
                                onChange={(e) => setPartialQuantity(e.target.value)}
                                placeholder={`Enter quantity (max ${maxQuantity})`}
                                step="0.001"
                                min="0"
                                max={maxQuantity}
                                className="partial-qty-input"
                                dir="ltr"
                            />
                            <small>Max: {maxQuantity} {unit}</small>
                        </div>
                    )}
                </div>
                <div className="modal-footer">
                    <button className="btn-cancel" onClick={onClose} disabled={isProcessing}>
                        <FontAwesomeIcon icon={faTimes} /> {translations?.cancel || 'Cancel'}
                    </button>
                    <button className="btn-approve" onClick={handleConfirm} disabled={isProcessing}>
                        {isProcessing ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheckCircle} />}
                        {translations?.confirmApprove || 'Confirm Approval'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Approval History Modal
// ==============================================
const ApprovalHistoryModal = ({ isOpen, onClose, history, translations, isRTL }) => {
    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                <div className="modal-header">
                    <h3><FontAwesomeIcon icon={faHistory} />{translations?.approvalHistory || 'Approval History'}</h3>
                    <button className="modal-close" onClick={onClose}><FontAwesomeIcon icon={faTimes} /></button>
                </div>
                <div className="modal-body">
                    {history.length === 0 ? (
                        <div className="no-history">No approval history found</div>
                    ) : (
                        <table className="history-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Approved By</th>
                                    <th>Quantity</th>
                                    <th>Yards</th>
                                    <th>Rolls</th>
                                    <th>Type</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((entry, idx) => (
                                    <tr key={idx}>
                                        <td>{new Date(entry.date).toLocaleString()}</td>
                                        <td>{entry.approved_by}</td>
                                        <td>{entry.quantity} {entry.unit}</td>
                                        <td>{entry.yards?.toFixed(2)} yds</td>
                                        <td>{entry.rolls || '-'}</td>
                                        <td>{entry.is_partial ? 'Partial' : 'Full'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                <div className="modal-footer">
                    <button className="btn-cancel" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Fetch Order Items Table Component
// ==============================================
const FetchOrderItemsTable = ({
    items,
    onItemChange,
    onRemove,
    onAdd,
    onApproveItem,
    onViewHistory,
    errors,
    disabled,
    translations,
    isRTL,
    user,
    shopId,
    showToast,
    currencySymbol,
    updatingItemId
}) => {
    const tableRef = useRef(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [productSearchTerms, setProductSearchTerms] = useState({});
    const [showProductDropdown, setShowProductDropdown] = useState({});
    const [loadingProducts, setLoadingProducts] = useState(false);
    const productSearchRefs = useRef({});

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const loadProducts = async () => {
        if (!user?.username || !shopId) return;
        setLoadingProducts(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/fab_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_records', username: user.username, shop_id: shopId, page: 1, limit: 100 })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setProducts(data.data || []);
                setFilteredProducts(data.data || []);
            }
        } catch (error) { console.error('Error loading products:', error); }
        finally { setLoadingProducts(false); }
    };

    useEffect(() => { if (user?.username && shopId) loadProducts(); }, [user?.username, shopId]);

    const handleProductSearch = async (index, term) => {
        setProductSearchTerms(prev => ({ ...prev, [index]: term }));
        if (!user?.username || !shopId) return;
        if (term.trim() === '') {
            setFilteredProducts(products);
            setShowProductDropdown(prev => ({ ...prev, [index]: true }));
            return;
        }
        const filtered = products.filter(product =>
            (product.product_name && product.product_name.toLowerCase().includes(term.toLowerCase())) ||
            (product.product_colorcode && product.product_colorcode.toLowerCase().includes(term.toLowerCase())) ||
            (product.batch_no && product.batch_no.toLowerCase().includes(term.toLowerCase()))
        );
        if (filtered.length > 0) {
            setFilteredProducts(filtered);
            setShowProductDropdown(prev => ({ ...prev, [index]: true }));
        } else {
            setLoadingProducts(true);
            try {
                const response = await fetch('https://ourzestapp.online/oz_backend/fab_products.php', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: 'get_records', username: user.username, shop_id: shopId, filters: { product_name: term }, page: 1, limit: 20 })
                });
                const data = await response.json();
                if (data.status === 'success') {
                    setFilteredProducts(data.data || []);
                    setShowProductDropdown(prev => ({ ...prev, [index]: data.data && data.data.length > 0 }));
                } else { setFilteredProducts([]); }
            } catch (error) { console.error('Error searching products:', error); setFilteredProducts([]); }
            finally { setLoadingProducts(false); }
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            Object.keys(productSearchRefs.current).forEach(index => {
                if (productSearchRefs.current[index] && !productSearchRefs.current[index].contains(event.target)) {
                    setShowProductDropdown(prev => ({ ...prev, [index]: false }));
                }
            });
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectProduct = async (index, product) => {
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/fab_products.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_record', id: product.id, username: user?.username, shop_id: shopId })
            });
            const data = await response.json();

            let productData;
            if (data.status === 'success' && data.data) {
                const fullProduct = data.data;
                productData = {
                    product_id: fullProduct.id,
                    product_name: fullProduct.product_name || '',
                    product_colorcode: fullProduct.product_colorcode || '',
                    batch_no: fullProduct.batch_no || '',
                    type: fullProduct.type || '',
                    brand: fullProduct.brand || '',
                    unit: fullProduct.unit || 'yards',
                    price: fullProduct.sell_price || fullProduct.price || '0'
                };
            } else {
                productData = {
                    product_id: product.id,
                    product_name: product.product_name || '',
                    product_colorcode: product.product_colorcode || '',
                    batch_no: product.batch_no || '',
                    type: product.type || '',
                    brand: product.brand || '',
                    unit: product.unit || 'yards',
                    price: product.sell_price || product.price || '0'
                };
            }

            onItemChange(index, 'product_id', productData.product_id);
            onItemChange(index, 'product_name', productData.product_name);
            onItemChange(index, 'product_colorcode', productData.product_colorcode);
            onItemChange(index, 'batch_no', productData.batch_no);
            onItemChange(index, 'type', productData.type);
            onItemChange(index, 'brand', productData.brand);
            onItemChange(index, 'unit', productData.unit);
            onItemChange(index, 'price', productData.price);

            const displayName = productData.product_name + (productData.product_colorcode ? ` - ${productData.product_colorcode}` : '');
            setProductSearchTerms(prev => ({ ...prev, [index]: displayName }));
            setShowProductDropdown(prev => ({ ...prev, [index]: false }));
            if (showToast) showToast('success', `Product "${productData.product_name}" selected`);
        } catch (error) {
            console.error('Error fetching product details:', error);
            const productData = {
                product_id: product.id, product_name: product.product_name || '',
                product_colorcode: product.product_colorcode || '', batch_no: product.batch_no || '',
                type: product.type || '', brand: product.brand || '', unit: product.unit || 'yards',
                price: product.sell_price || product.price || '0'
            };
            onItemChange(index, 'product_id', productData.product_id);
            onItemChange(index, 'product_name', productData.product_name);
            onItemChange(index, 'product_colorcode', productData.product_colorcode);
            onItemChange(index, 'batch_no', productData.batch_no);
            onItemChange(index, 'type', productData.type);
            onItemChange(index, 'brand', productData.brand);
            onItemChange(index, 'unit', productData.unit);
            onItemChange(index, 'price', productData.price);
            const displayName = productData.product_name + (productData.product_colorcode ? ` - ${productData.product_colorcode}` : '');
            setProductSearchTerms(prev => ({ ...prev, [index]: displayName }));
            setShowProductDropdown(prev => ({ ...prev, [index]: false }));
        }
    };

    const calculateTotal = (price, quantity) => {
        const p = parseFloat(price) || 0;
        const q = parseFloat(quantity) || 0;
        return (p * q).toFixed(0);
    };

    const getProductDisplayName = (item) => {
        if (item.product_name && item.product_colorcode) return `${item.product_name} - ${item.product_colorcode}`;
        else if (item.product_name) return item.product_name;
        else if (item.product_colorcode) return item.product_colorcode;
        return '';
    };

    const getApproveStatusBadge = (item) => {
        const approvedQty = parseFloat(item.approved_quantity) || 0;
        const totalQty = parseFloat(item.ordered_quantity) || 0;
        
        if (approvedQty >= totalQty && totalQty > 0) {
            return { text: translations?.approved || 'Approved', color: '#10b981', bg: '#10b98120' };
        } else if (approvedQty > 0) {
            return { text: translations?.partiallyApproved || 'Partially Approved', color: '#f59e0b', bg: '#f59e0b20' };
        }
        return { text: translations?.notApproved || 'Not Approved', color: '#ef4444', bg: '#ef444420' };
    };

    const isItemUpdating = (itemId) => updatingItemId === itemId;

    // Desktop view
    return (
        <div className="fetch-items-table-container">
            <div className="fetch-table-header-actions">
                <h4><FontAwesomeIcon icon={faBoxes} />{translations?.product || 'Products'}</h4>
                <button type="button" className="fetch-btn-add-item" onClick={() => onAdd()} disabled={disabled}>
                    <FontAwesomeIcon icon={faPlus} /><span>{translations?.addItem || 'Add Item'}</span>
                </button>
            </div>
            <div className="fetch-table-wrapper" ref={tableRef}>
                <table className="fetch-items-table">
                    <thead>
                        <tr>
                            <th>Order #</th>
                            <th>Supplier</th>
                            <th>Order Date</th>
                            <th>{translations?.approveStatus || 'Status'}</th>
                            <th className="fetch-product-col">{translations?.productName || 'Product'} & Color</th>
                            <th>Batch #</th>
                            <th>Type/Brand</th>
                            <th>Unit</th>
                            <th className="fetch-price-col">{translations?.price || 'Price'}</th>
                            <th className="fetch-qty-col">Ordered Qty</th>
                            <th className="fetch-approved-col">Approved</th>
                            <th>Remaining</th>
                            <th className="fetch-total-col">{translations?.totalAmount || 'Total'}</th>
                            <th className="fetch-action-col">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => {
                            const approveStatus = getApproveStatusBadge(item);
                            const isFullyApproved = parseFloat(item.approved_quantity) >= parseFloat(item.ordered_quantity);
                            const isUpdating = isItemUpdating(item.id);
                            
                            return (
                                <tr key={item.id || index} className={errors[`product_name_${index}`] ? 'fetch-error-row' : ''}>
                                    <td><div className="fetch-table-field"><input type="text" value={item.order_number || ''} readOnly className="fetch-readonly-input" /></div></td>
                                    <td><div className="fetch-table-field"><input type="text" value={item.supplier_name || ''} readOnly className="fetch-readonly-input" /></div></td>
                                    <td><div className="fetch-table-field"><input type="text" value={item.order_date || ''} readOnly className="fetch-readonly-input" /></div></td>
                                    <td>
                                        <div className="fetch-table-field">
                                            <div className="fetch-status-badge" style={{ background: approveStatus.bg, color: approveStatus.color, padding: '4px 8px', borderRadius: '4px', display: 'inline-block' }}>
                                                {approveStatus.text}
                                            </div>
                                            {parseFloat(item.approved_quantity) > 0 && (
                                                <div className="fetch-approved-info"><small>{item.approved_quantity} / {item.ordered_quantity} approved</small></div>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="fetch-table-field fetch-product-search-field" ref={el => productSearchRefs.current[index] = el}>
                                            <div className="fetch-product-search-container">
                                                <input type="text"
                                                    value={productSearchTerms[index] !== undefined ? productSearchTerms[index] : getProductDisplayName(item)}
                                                    onChange={(e) => handleProductSearch(index, e.target.value)}
                                                    onFocus={() => { setShowProductDropdown(prev => ({ ...prev, [index]: true })); if (!productSearchTerms[index]) setFilteredProducts(products); }}
                                                    placeholder={translations?.searchProduct || 'Search product...'} disabled={disabled}
                                                    dir={isRTL ? 'rtl' : 'ltr'} autoComplete="off" />
                                                {loadingProducts && <div className="fetch-search-loading-small"><FontAwesomeIcon icon={faSpinner} spin /></div>}
                                                {showProductDropdown[index] && (
                                                    <div className="fetch-product-dropdown-table">
                                                        {filteredProducts.length > 0 ? (
                                                            filteredProducts.map(product => (
                                                                <div key={product.id} className="fetch-product-option" onClick={() => selectProduct(index, product)}>
                                                                    <div className="fetch-product-name">{product.product_name}{product.product_colorcode && ` - ${product.product_colorcode}`}</div>
                                                                    <div className="fetch-product-batch">Batch: {product.batch_no || 'N/A'}</div>
                                                                    <div className="fetch-product-stock">Stock: {product.total_yards || 0} yds</div>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div className="fetch-product-dropdown-table fetch-empty"><FontAwesomeIcon icon={faExclamationTriangle} />{translations?.noProductsFound || 'No products found'}</div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td><div className="fetch-table-field"><input type="text" value={item.batch_no || ''} onChange={(e) => onItemChange(index, 'batch_no', e.target.value)} disabled={disabled} /></div></td>
                                    <td><div className="fetch-table-field"><input type="text" value={item.type || ''} onChange={(e) => onItemChange(index, 'type', e.target.value)} placeholder="Type" disabled={disabled} /></div></td>
                                    <td>
                                        <div className="fetch-table-field">
                                            <select value={item.unit || 'yards'} onChange={(e) => onItemChange(index, 'unit', e.target.value)} disabled={disabled}>
                                                <option value="yards">Yards</option>
                                                <option value="meters">Meters</option>
                                            </select>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="fetch-table-field">
                                            <input type="number" value={item.price} onChange={(e) => onItemChange(index, 'price', e.target.value)} step="1" min="0" placeholder="0" disabled={disabled} />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="fetch-table-field">
                                            <input type="number" value={item.ordered_quantity} onChange={(e) => onItemChange(index, 'ordered_quantity', e.target.value)} step="0.001" min="0" disabled={disabled} />
                                        </div>
                                    </td>
                                    <td><div className="fetch-table-field"><span className="fetch-approved-quantity">{item.approved_quantity || 0}</span></div></td>
                                    <td><div className="fetch-table-field"><span className="fetch-remaining-quantity">{item.remaining_quantity || item.ordered_quantity || 0}</span></div></td>
                                    <td><div className="fetch-table-field"><span className="fetch-total-amount">{currencySymbol} {calculateTotal(item.price, item.ordered_quantity)}</span></div></td>
                                    <td>
                                        <div className="fetch-table-actions">
                                            <button type="button" className="fetch-table-history-btn" onClick={() => onViewHistory(item)} title="View History" disabled={disabled}>
                                                <FontAwesomeIcon icon={faHistory} />
                                            </button>
                                            <button type="button" className="fetch-table-remove-btn" onClick={() => onRemove(index)} disabled={disabled || items.length === 1} title={translations?.remove || 'Remove'}>
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                            {!isFullyApproved && (
                                                <button type="button" className="fetch-table-approve-btn" onClick={() => onApproveItem(item)} disabled={disabled || isUpdating} title={translations?.approveStatus || 'Approve'}>
                                                    {isUpdating ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheckCircle} />}
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ==============================================
// Main FetchOrder Component
// ==============================================
const FetchOrder = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();

    const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem('ourzest_language') || 'en');
    const [shopId, setShopId] = useState(() => currentShop?.shop_id || localStorage.getItem('current_shop_id') || '');
    const [allItems, setAllItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [updatingItemId, setUpdatingItemId] = useState(null);

    const [filters, setFilters] = useState({ order_number: '', supplier_name: '', start_date: '', end_date: '' });
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });
    const [translations, setTranslations] = useState({});
    const [isTranslating, setIsTranslating] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', message: '', direction: 'ltr', progress: 100, timeLeft: 3 });
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [selectedApprovalItem, setSelectedApprovalItem] = useState(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyData, setHistoryData] = useState([]);

    const toastTimerRef = useRef(null);
    const [errors, setErrors] = useState({});
    const abortControllerRef = useRef(null);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        setToast({ show: true, type, message, direction: isRTL ? 'rtl' : 'ltr', progress: 100, timeLeft: 3 });
        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            setToast(prev => ({ ...prev, progress: (timeLeft / 3) * 100, timeLeft: parseFloat(timeLeft.toFixed(1)) }));
            if (timeLeft <= 0) { clearInterval(timer); setToast(prev => ({ ...prev, show: false })); }
        }, 100);
        toastTimerRef.current = timer;
    }, [currentLanguage]);

    useEffect(() => {
        if (currentShop) {
            setShopId(currentShop.shop_id);
        }
    }, [currentShop]);

    useEffect(() => {
        if (shopId && user?.username) {
            loadItems();
        }
    }, [shopId, user?.username, pagination.page, filters]);

    const loadItems = async () => {
        if (!user?.username || !shopId) {
            if (!shopId) setShowShopSelector(true);
            return;
        }
        setLoading(true);
        if (abortControllerRef.current) abortControllerRef.current.abort();
        abortControllerRef.current = new AbortController();
        try {
            const payload = {
                action: 'get_all_unapproved_items',
                username: user.username,
                shop_id: shopId,
                page: pagination.page,
                limit: pagination.limit,
                filters: {}
            };
            if (filters.order_number) payload.filters.order_number = filters.order_number;
            if (filters.supplier_name) payload.filters.supplier_name = filters.supplier_name;
            if (filters.start_date) payload.filters.start_date = filters.start_date;
            if (filters.end_date) payload.filters.end_date = filters.end_date;

            const response = await fetch('https://ourzestapp.online/oz_backend/fabrics_approval.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                signal: abortControllerRef.current.signal
            });
            const data = await response.json();
            if (data.status === 'success') {
                setAllItems(data.data || []);
                setPagination(prev => ({ ...prev, total: data.total || 0 }));
            } else {
                showToast('error', data.message || 'Failed to load items');
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Error loading items:', error);
                showToast('error', 'Network error occurred');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        loadItems();
    };

    const clearFilters = () => {
        setFilters({ order_number: '', supplier_name: '', start_date: '', end_date: '' });
        setPagination(prev => ({ ...prev, page: 1 }));
        setTimeout(() => loadItems(), 100);
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleItemChange = (index, field, value) => {
        const updatedItems = [...allItems];
        updatedItems[index] = { ...updatedItems[index], [field]: value };
        setAllItems(updatedItems);
    };

    const handleRemoveItem = (index) => {
        if (window.confirm('Are you sure you want to remove this item?')) {
            const updatedItems = allItems.filter((_, i) => i !== index);
            setAllItems(updatedItems);
        }
    };

    const handleAddItem = () => {
        const newItem = {
            id: 'new_' + Date.now(),
            order_id: '',
            order_number: '',
            supplier_name: '',
            order_date: '',
            product_id: '',
            product_name: '',
            product_colorcode: '',
            batch_no: '',
            type: '',
            brand: '',
            unit: 'yards',
            price: '0',
            ordered_quantity: '0',
            approved_quantity: 0,
            remaining_quantity: 0,
            approval_status: 'Pending'
        };
        setAllItems([...allItems, newItem]);
    };

    const handleApproveItem = async (item, approvalData) => {
        setUpdating(true);
        setUpdatingItemId(item.id);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/fabrics_approval.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'approve_item',
                    username: user?.username,
                    shop_id: shopId,
                    item: item,
                    approval_quantity: approvalData.quantity,
                    is_partial: approvalData.type === 'partial'
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', `Successfully approved ${approvalData.quantity} ${item.unit || 'units'}`);
                loadItems();
            } else {
                showToast('error', data.message || 'Failed to approve item');
            }
        } catch (error) {
            console.error('Error approving item:', error);
            showToast('error', 'Network error occurred');
        } finally {
            setUpdating(false);
            setUpdatingItemId(null);
            setShowApprovalModal(false);
            setSelectedApprovalItem(null);
        }
    };

    const handleViewHistory = async (item) => {
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/fabrics_approval.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_approval_history',
                    username: user?.username,
                    shop_id: shopId,
                    order_item_id: item.id
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setHistoryData(data.data || []);
                setShowHistoryModal(true);
            } else {
                showToast('error', 'Failed to load history');
            }
        } catch (error) {
            console.error('Error loading history:', error);
            showToast('error', 'Network error occurred');
        }
    };

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                showToast('success', 'Shop switched successfully');
                loadItems();
            } else {
                showToast('error', 'Failed to switch shop');
            }
        } catch (error) {
            showToast('error', 'Failed to switch shop');
        }
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`fetch-order-independent ${isRTL ? 'rtl' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />
            
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />
            
            <ApprovalModal
                isOpen={showApprovalModal}
                onClose={() => { setShowApprovalModal(false); setSelectedApprovalItem(null); }}
                item={selectedApprovalItem}
                onConfirm={(approvalData) => handleApproveItem(selectedApprovalItem, approvalData)}
                translations={translations}
                isRTL={isRTL}
                loading={updating}
            />
            
            <ApprovalHistoryModal
                isOpen={showHistoryModal}
                onClose={() => setShowHistoryModal(false)}
                history={historyData}
                translations={translations}
                isRTL={isRTL}
            />

            <div className="fetch-header-modern">
                <div className="fetch-header-content">
                    <div className="fetch-header-icon"><FontAwesomeIcon icon={faSearch} /></div>
                    <div className="fetch-header-text">
                        <h1>{translations?.fetchOrders || 'Fetch Orders'}</h1>
                        <p>{translations?.pendingApproval || 'Pending Approval'}</p>
                    </div>
                </div>
                {!currentShop && (
                    <div className="shop-indicator-modern no-shop">
                        <div className="shop-badge"><FontAwesomeIcon icon={faExclamationTriangle} /><span>{translations?.noShopSelected || 'No shop selected'}</span></div>
                        <button className="btn-modern btn-switch-shop" onClick={() => setShowShopSelector(true)}><FontAwesomeIcon icon={faExchangeAlt} /><span>{translations?.switchShop || 'Switch Shop'}</span></button>
                    </div>
                )}
            </div>

            <div className="fetch-form-section">
                <div className="fetch-section-header">
                    <h3 className="fetch-section-title"><FontAwesomeIcon icon={faFilter} />{translations?.filter || 'Filter Items'}</h3>
                </div>
                <form onSubmit={handleFilterSubmit} className="fetch-filter-row">
                    <div className="fetch-filter-group">
                        <div className="fetch-input-container">
                            <FontAwesomeIcon icon={faFileInvoice} className="fetch-input-icon" />
                            <input type="text" name="order_number" value={filters.order_number} onChange={handleFilterChange} placeholder=" " disabled={loading} dir={isRTL ? 'rtl' : 'ltr'} />
                            <label className="fetch-input-label">{translations?.orderNumber || 'Order Number'}</label>
                        </div>
                    </div>
                    <div className="fetch-filter-group">
                        <div className="fetch-input-container">
                            <FontAwesomeIcon icon={faUser} className="fetch-input-icon" />
                            <input type="text" name="supplier_name" value={filters.supplier_name} onChange={handleFilterChange} placeholder=" " disabled={loading} dir={isRTL ? 'rtl' : 'ltr'} />
                            <label className="fetch-input-label">{translations?.supplierName || 'Supplier'}</label>
                        </div>
                    </div>
                    <div className="fetch-filter-group">
                        <div className="fetch-input-container">
                            <FontAwesomeIcon icon={faCalendarAlt} className="fetch-input-icon" />
                            <input type="date" name="start_date" value={filters.start_date} onChange={handleFilterChange} placeholder=" " disabled={loading} className="no-rtl" />
                            <label className="fetch-input-label">Start Date</label>
                        </div>
                    </div>
                    <div className="fetch-filter-group">
                        <div className="fetch-input-container">
                            <FontAwesomeIcon icon={faCalendarAlt} className="fetch-input-icon" />
                            <input type="date" name="end_date" value={filters.end_date} onChange={handleFilterChange} placeholder=" " disabled={loading} className="no-rtl" />
                            <label className="fetch-input-label">End Date</label>
                        </div>
                    </div>
                    <div className="fetch-filter-actions">
                        <button type="submit" className="fetch-btn-modern fetch-btn-primary fetch-btn-compact" disabled={loading}>
                            {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <><FontAwesomeIcon icon={faSearch} /><span>{translations?.search || 'Search'}</span></>}
                        </button>
                        <button type="button" className="fetch-btn-modern fetch-btn-secondary fetch-btn-compact" onClick={clearFilters} disabled={loading}>
                            <FontAwesomeIcon icon={faTimes} /><span>{translations?.clear || 'Clear'}</span>
                        </button>
                        <button type="button" className="fetch-btn-modern fetch-btn-refresh fetch-btn-compact" onClick={loadItems} disabled={loading}>
                            <FontAwesomeIcon icon={faSync} spin={loading} /><span>{translations?.refresh || 'Refresh'}</span>
                        </button>
                    </div>
                </form>
            </div>

            <div className="fetch-form-section">
                {loading ? (
                    <div className="fetch-loading-container"><div className="fetch-loading-spinner"></div><p>{translations?.loading || 'Loading...'}</p></div>
                ) : allItems.length === 0 ? (
                    <div className="fetch-no-data-container"><FontAwesomeIcon icon={faBoxes} size="3x" /><p>{translations?.noOrders || 'No pending items found'}</p></div>
                ) : (
                    <>
                        <FetchOrderItemsTable
                            items={allItems}
                            onItemChange={handleItemChange}
                            onRemove={handleRemoveItem}
                            onAdd={handleAddItem}
                            onApproveItem={(item) => { setSelectedApprovalItem(item); setShowApprovalModal(true); }}
                            onViewHistory={handleViewHistory}
                            errors={errors}
                            disabled={updating}
                            translations={translations}
                            isRTL={isRTL}
                            user={user}
                            shopId={shopId}
                            showToast={showToast}
                            currencySymbol="Rs"
                            updatingItemId={updatingItemId}
                        />
                        {pagination.total > pagination.limit && (
                            <div className="fetch-pagination">
                                <button onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page === 1 || loading} className="fetch-pagination-btn">{translations?.previous || 'Previous'}</button>
                                <span className="fetch-pagination-info">{translations?.page || 'Page'} {pagination.page} {translations?.of || 'of'} {Math.ceil(pagination.total / pagination.limit)}</span>
                                <button onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit) || loading} className="fetch-pagination-btn">{translations?.next || 'Next'}</button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {updating && (
                <div className="fetch-loading-overlay">
                    <div className="fetch-loading-content"><div className="fetch-loading-spinner"></div><p>{translations?.updating || 'Updating...'}</p></div>
                </div>
            )}
        </div>
    );
};

export default FetchOrder;